// Derived values, inheritance and validation for the MMH Products form.
// Pure functions only — reused by the wizard steps, the review screen and the
// mock layer.

import {
  ACCEPTED_IMAGE_TYPES,
  IMAGE_CATEGORY_WITH_SUB,
  INHERITABLE_IMAGE_FIELDS,
  MAX_IMAGE_SIZE_MB,
  ROOM_FIELD_NAMES,
} from "./constants";

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Plot area and build-up area follow width, length and floors, exactly as the
 * legacy calc() computed them. They are never typed, so the three numbers
 * cannot drift apart.
 */
export const deriveAreas = ({ width, length, floor }) => {
  const w = toNumber(width);
  const l = toNumber(length);
  const f = toNumber(floor);

  return {
    plotArea: w !== null && l !== null ? String(w * l) : "",
    buildUpArea: w !== null && l !== null && f !== null ? String(w * l * f) : "",
  };
};

/**
 * The legacy calc() title:
 *   28X62 house plan- 1736 sqft Residential house design At Baraut [UP]
 *
 * It wrote a literal "house design" and string-replaced it later once an
 * image category was known. We resolve the display picture's category up
 * front, so the title is right the first time.
 */
export const buildProjectName = (form, categoryName, dpImageCategory = "") => {
  const { width, length, plotArea, categoryType, clientLocation } = form;

  if (!width || !length || !plotArea || !categoryName || !categoryType || !clientLocation) {
    return "";
  }

  const descriptor = dpImageCategory?.trim() || "house design";

  return `${width}X${length} ${categoryType} plan- ${plotArea} sqft ${categoryName} ${descriptor} At ${clientLocation}`;
};

export const getDpImage = (imageItems = []) =>
  imageItems.find((item) => item.isDp) || imageItems[0] || null;

/**
 * Guarantees exactly one display picture.
 *
 * The admin never has to pick one: the first image becomes the DP on its own
 * and stays that way unless they choose another. This runs on every change to
 * the image list — add, remove, reorder, explicit pick — so there is a single
 * rule rather than one special case per action, which is how a list with zero
 * or two DPs used to become possible.
 */
export const ensureSingleDp = (items = []) => {
  if (!items.length) return items;

  const flagged = items.findIndex((item) => item.isDp);
  const dpIndex = flagged === -1 ? 0 : flagged;

  // Leave the array alone when it already holds exactly one DP, so React
  // does not re-render on every unrelated edit.
  const alreadyCorrect =
    flagged !== -1 && items.filter((item) => item.isDp).length === 1;
  if (alreadyCorrect) return items;

  return items.map((item, index) => ({ ...item, isDp: index === dpIndex }));
};

export const imageNeedsSubCategory = (category) => category === IMAGE_CATEGORY_WITH_SUB;

// ---- Image detail inheritance -----------------------------------------

/** The inheritable slice of one image's details. */
export const inheritableFrom = (item) =>
  INHERITABLE_IMAGE_FIELDS.reduce((acc, field) => {
    const value = item[field];
    acc[field] = Array.isArray(value) ? [...value] : value;
    return acc;
  }, {});

/**
 * Copies the details of the image at `sourceIndex` onto every later image
 * the admin has not edited yet.
 *
 * This is what saves the repetitive typing: tag the first photo, and the rest
 * arrive pre-filled. The moment an image is edited it stops inheriting, so a
 * later change upstream never overwrites work already done on it.
 */
export const cascadeImageDetails = (items, sourceIndex) => {
  const source = items[sourceIndex];
  if (!source) return items;

  const inherited = inheritableFrom(source);

  return items.map((item, index) => {
    if (index <= sourceIndex) return item;
    // `touched` alone decides it. An image that already inherited once has
    // filled fields but is still not the admin's own work, so it keeps
    // following the image above — otherwise only the first keystroke typed
    // there would ever reach it.
    if (item.touched) return item;
    return { ...item, ...inherited, keywords: [...(inherited.keywords || [])] };
  });
};

// ---- Upload guards ----------------------------------------------------

export const validateUploadFile = (file) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return `"${file.name}" is not a supported file. Use JPG, PNG, WEBP or PDF.`;
  }
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return `"${file.name}" is larger than ${MAX_IMAGE_SIZE_MB}MB.`;
  }
  return null;
};

// ---- Step validation --------------------------------------------------
// Mirrors what the legacy form marked required.

export const validateDetailsStep = (form) => {
  const errors = {};

  const required = {
    projectCode: "Project code",
    projectName: "Product name",
    direction: "Direction",
    clientLocation: "Client location",
    clientName: "Client name",
    category: "Category",
    categoryType: "Category type",
    projectCost: "Project cost",
    productCode: "Product code",
    planType: "Plan type",
  };

  Object.entries(required).forEach(([field, label]) => {
    if (!String(form[field] ?? "").trim()) {
      errors[field] = `${label} is required`;
    }
  });

  [["width", "Width"], ["length", "Length"], ["floor", "Floor"]].forEach(([field, label]) => {
    const n = toNumber(form[field]);
    if (n === null || n <= 0) {
      errors[field] = `${label} is required`;
    }
  });

  if (!form.projectType) {
    errors.projectType = "Select a project type";
  }

  if (!String(form.pincode ?? "").trim()) {
    errors.pincode = "Pincode is required";
  } else if (!/^\d{6}$/.test(String(form.pincode).trim())) {
    errors.pincode = "Pincode must be 6 digits";
  }

  if (String(form.addLink ?? "").trim() && !/^https?:\/\/\S+$/i.test(String(form.addLink).trim())) {
    errors.addLink = "Enter a full URL starting with http:// or https://";
  }

  return errors;
};

export const validateImagesStep = (imageItems = []) => {
  const errors = { form: {}, images: {} };

  if (!imageItems.length) {
    errors.form.images = "Add at least one image before continuing";
    return errors;
  }
  imageItems.forEach((item) => {
    const itemErrors = {};

    if (!String(item.title ?? "").trim()) itemErrors.title = "Image title is required";
    if (!item.category) itemErrors.category = "Select a category";
    if (imageNeedsSubCategory(item.category) && !item.subCategory) {
      itemErrors.subCategory = "Select a sub category";
    }
    if (!item.style) itemErrors.style = "Select a style";

    if (Object.keys(itemErrors).length) {
      errors.images[item.uid] = itemErrors;
    }
  });

  return errors;
};

export const imagesStepHasErrors = (errors) =>
  Boolean(Object.keys(errors.form).length || Object.keys(errors.images).length);

/** Room counts always go out as numbers; the legacy columns are ints. */
export const serialiseRoomCounts = (form) =>
  ROOM_FIELD_NAMES.reduce((acc, name) => {
    acc[name] = toNumber(form[name]) ?? 0;
    return acc;
  }, {});
